import { el, qs } from '../utils/dom.js';
import { TOOLS } from '../utils/constants.js';

/**
 * CommandPalette — Ctrl+P searchable action list (like VS Code).
 *
 * Lists all menu actions, tool switches, view controls, and recent files.
 * Fuzzy search narrows results as you type. Enter executes, Escape closes.
 */
export class CommandPalette {
  constructor(state, actions) {
    this.state = state;
    this._actions = actions; // Array of { label, action, shortcut?, category? }
    this._overlay = null;
  }

  /** Register additional actions dynamically. */
  addAction(label, action, shortcut, category) {
    this._actions.push({ label, action, shortcut: shortcut || '', category: category || 'General' });
  }

  toggle() {
    if (this._overlay) { this.close(); return; }
    this.open();
  }

  open() {
    if (this._overlay) return;

    const overlay = el('div', { className: 'cmd-palette-overlay', onClick: (e) => {
      if (e.target === overlay) this.close();
    }});

    const container = el('div', { className: 'cmd-palette' });

    const input = el('input', {
      type: 'text',
      className: 'cmd-palette-input',
      placeholder: 'Type a command...',
    });
    container.appendChild(input);

    const list = el('div', { className: 'cmd-palette-list' });
    container.appendChild(list);

    overlay.appendChild(container);
    document.body.appendChild(overlay);
    this._overlay = overlay;

    // Render initial list
    this._renderList(list, '');

    input.addEventListener('input', () => {
      this._renderList(list, input.value.trim().toLowerCase());
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Enter') {
        const first = list.querySelector('.cmd-palette-item');
        if (first) first.click();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const active = list.querySelector('.cmd-palette-item.active');
        const next = active?.nextElementSibling || list.querySelector('.cmd-palette-item');
        if (active) active.classList.remove('active');
        if (next) { next.classList.add('active'); next.scrollIntoView({ block: 'nearest' }); }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const active = list.querySelector('.cmd-palette-item.active');
        const prev = active?.previousElementSibling || list.querySelector('.cmd-palette-item:last-child');
        if (active) active.classList.remove('active');
        if (prev) { prev.classList.add('active'); prev.scrollIntoView({ block: 'nearest' }); }
      }
    });

    input.focus();
  }

  close() {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
  }

  _renderList(container, query) {
    container.innerHTML = '';

    let filtered = this._actions;
    if (query) {
      filtered = this._actions.filter(a => {
        const text = `${a.category || ''} ${a.label}`.toLowerCase();
        // Simple fuzzy: all query chars appear in order
        let qi = 0;
        for (let i = 0; i < text.length && qi < query.length; i++) {
          if (text[i] === query[qi]) qi++;
        }
        return qi === query.length;
      });
    }

    // Limit display
    const maxShow = 20;
    for (let i = 0; i < Math.min(filtered.length, maxShow); i++) {
      const action = filtered[i];
      const item = el('div', {
        className: `cmd-palette-item${i === 0 ? ' active' : ''}`,
        onClick: () => { this.close(); action.action(); },
      });

      const labelEl = el('span', { className: 'cmd-palette-label' });
      if (action.category) {
        labelEl.appendChild(el('span', { className: 'cmd-palette-category' }, action.category + ' > '));
      }
      labelEl.appendChild(document.createTextNode(action.label));
      item.appendChild(labelEl);

      if (action.shortcut) {
        item.appendChild(el('span', { className: 'cmd-palette-shortcut' }, action.shortcut));
      }

      container.appendChild(item);
    }

    if (filtered.length === 0) {
      container.appendChild(el('div', { className: 'cmd-palette-empty' }, 'No matching commands'));
    }
  }
}

/**
 * Build the standard action list from TileForge state and components.
 */
export function buildActions(state, opts) {
  const { fileManager, compositor, commandHistory, toolManager, generatorPanel, newMapDialog,
          exportDialog, resizeDialog, menuBar } = opts;

  const actions = [];
  const add = (label, action, shortcut, category) =>
    actions.push({ label, action, shortcut: shortcut || '', category: category || 'General' });

  // File
  add('New Map', () => newMapDialog?.show(), 'Ctrl+N', 'File');
  add('Open File', () => fileManager.openFile(), 'Ctrl+O', 'File');
  add('Save', () => fileManager.saveFile(), 'Ctrl+S', 'File');
  add('Save As', () => fileManager.saveFileAs(), 'Ctrl+Shift+S', 'File');

  // Edit
  add('Undo', () => { commandHistory.undo(); compositor.requestRender(); }, 'Ctrl+Z', 'Edit');
  add('Redo', () => { commandHistory.redo(); compositor.requestRender(); }, 'Ctrl+Y', 'Edit');

  // Tools
  for (const [key, name] of Object.entries(TOOLS)) {
    const shortcuts = { PENCIL: 'B', RECT: 'R', FILL: 'G', ERASER: 'E', SELECT: 'S',
                        PROPERTY: 'P', OBJECT: 'O', TERRAIN: 'T', SCATTER: 'D', LINE: 'L' };
    add(`${key.charAt(0) + key.slice(1).toLowerCase()} Tool`, () => state.set('activeTool', name), shortcuts[key] || '', 'Tools');
  }

  // View
  add('Toggle Grid', () => { state.set('showGrid', !state.get('showGrid')); compositor.requestRender(); }, '#', 'View');
  add('Zoom In', () => { state.set('zoom', Math.min(8, state.get('zoom') + 0.25)); compositor.requestRender(); }, '+', 'View');
  add('Zoom Out', () => { state.set('zoom', Math.max(0.25, state.get('zoom') - 0.25)); compositor.requestRender(); }, '-', 'View');
  add('Reset View', () => { state.batch({ zoom: 1, scrollX: 0, scrollY: 0 }); compositor.requestRender(); }, 'Ctrl+0', 'View');

  // Generate
  add('Generate Map', () => generatorPanel?.show(), '', 'Generate');
  add('Resize Map', () => resizeDialog?.show(), '', 'Map');
  add('Export as PNG', () => exportDialog?.export(), '', 'Export');

  return actions;
}
